import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { withNamespaces } from 'react-i18next';
import styled from 'styled-components';

import { Link, routerStore } from 'core/routing';
import { ComponentRegistry } from 'core/index';
import { Avatar, Tooltip } from 'components';

import DocumentHead from 'legacyComponents/documentHead';
import { media } from 'legacyComponents/styled/styleUtils';
import { TopBar } from 'legacyComponents/layout/topBar';
import { ProfileIndicator } from 'legacyComponents/layout/profileIndicator';

import { imageSrc } from 'apps/file/utils';
import { clientStore } from 'apps/client/app';
import { conf as globalConf } from 'apps/config.yaml';
import {
  MainWrapper,
  ProfileNoContent,
  Indicator,
} from 'legacyComponents/styled/lib';

import Header from 'apps/components/header/header';
import { Tags, TagsIndicator } from 'apps/tag/index';
import { FourOhFour } from 'apps/fourOhFour/index';

import { ProfileOverview } from 'apps/profile/views/overview';
import { ProfilesAutoComplete } from 'apps/profile/views/autocomplete';
import { ProfileItem } from 'apps/profile/views/item';

ComponentRegistry.add('profiles.itemComponent', ProfileItem);
ComponentRegistry.add('profile.autocompleteElement', ProfilesAutoComplete);

const ProfileIntro = props => {
  const { appManager } = props;
  const { params } = routerStore;

  useEffect(
    () => () => {
      clientStore.SetFooterHidden(false);
    },
    [],
  );

  useEffect(() => {
    if (params.get('impersonate') === true && appManager.profile) {
      appManager.Impersonate();
    }
  }, [appManager.profile]);

  if (appManager.queries.get('profile')?.notFound) {
    return <FourOhFour type='Intro' />;
  }
  if (!appManager.profile) return null;
  const { profile, projectsConn, storiesConn, collectionsConn, threadsConn } =
    appManager;
  const { username } = profile?.user || '';
  const dataTip = props.t(
    'See all Organisations that {{ username }} is part of',
    {
      username,
    },
  );

  if (profile === null) {
    if (appManager.loading) {
      return (
        <main style={{ marginTop: '44px', marginRight: '11px' }}>
          <DocumentHead title={`${params.get('profile')}`} />
          <Header />
          <TopBar>
            <TagsIndicator />
            <AvatarWrapper>
              <AvatarIndicator />
              <AvatarIndicator />
              <AvatarIndicator />
            </AvatarWrapper>
          </TopBar>
          <ProfileIndicator />
        </main>
      );
    }
    // TODO: this should be some kind of error page. Profile == null but
    // the appManager is not loading (not going to change).
    return null;
  }
  const { canUpdate, skills } = profile;
  const profileInitiatives = Array.from(
    appManager.initiativesConn.arrayApp.apps.values(),
  );
  const displayInitiatives = profileInitiatives.slice(0, 3);
  const showMoreFunction = profile.ShowMembers || profile.ShowInitiatives;

  const noProjects = !canUpdate && projectsConn.totalCount === 0;
  const noStories = !canUpdate && storiesConn.totalCount === 0;
  const noCollections = !canUpdate && collectionsConn.totalCount === 0;
  const noThreads = !canUpdate && threadsConn.arrayApp.length === 0;

  const noContent = [];
  if (noProjects && noStories && noCollections && noThreads) {
    // TODO (david): This probably should be inside a useEffect
    clientStore.SetFooterHidden(true);
    if (!canUpdate) {
      noContent.push(
        <ProfileNoContent key={`${profile.fullName}-no-content`}>
          {profile.fullName.split(' ')[0]} is just getting started on
          Wikifactory. Follow or check back later for Projects, Stories and
          Collections!
        </ProfileNoContent>,
      );
    }
  }

  return (
    <>
      <DocumentHead
        title={`${profile.fullName} (${params.get('profile')})`}
        description={profile.bio}
        imagePath={imageSrc(
          profile.avatar,
          '',
          globalConf.imgSizes.profile.large,
        )}
      />
      <Header />
      <TopBar>
        <Tooltip delayShow={500} />
        <Tags type='Skills' tags={skills} app={profile} />
        <Initiatives>
          {profileInitiatives.length > 3 && (
            <Initiative
              onClick={showMoreFunction}
              condition={profileInitiatives.length > 3}
              data-tip={dataTip}
            >
              <MoreInitiatives>+{profileInitiatives.length}</MoreInitiatives>
            </Initiative>
          )}
          {displayInitiatives.map((initiative, index) => {
            const tooltip = `${initiative.title} (${initiative.slug})`;
            const linkName = 'initiative';
            const linkParams = { initiative: initiative.slug };
            return (
              <Initiative key={index} data-tip={tooltip}>
                <Link name={linkName} params={linkParams}>
                  <InitiativeAvatar
                    src={initiative.avatar?.url}
                    name={initiative.title}
                  />
                </Link>
              </Initiative>
            );
          })}
        </Initiatives>
      </TopBar>
      <MainWrapper>
        <ProfileOverview profileManager={appManager} />
      </MainWrapper>
    </>
  );
};

const Initiative = styled.li`
  display: inline-block;
  &:not(:first-child) {
    margin-left: ${props => props.theme.spacing.default};
  }
`;

const Initiatives = styled.ul`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  ${media.s`
    display: none;
  `}
`;

const InitiativeAvatar = styled(Avatar)`
  width: 34px;
  height: 34px;
  cursor: pointer;
  > img {
    height: 100%;
    width: 100%;
  }
  &:hover {
    opacity: 0.9;
  }
  ${media.s`
    width: 30px;
    height: 30px;
  `}
`;

const MoreInitiatives = styled(InitiativeAvatar)`
  background-image: none;
  background-color: rgba(0, 0, 0, 0.2);
  color: ${props => props.theme.color.wfWhite};
  font-weight: ${props => props.theme.font.weight.xbold};
  font-size: ${props => props.theme.font.size.small};
`;

const AvatarWrapper = styled.div`
  float: right;
  margin-top: 0px;
`;

const AvatarIndicator = styled(Indicator)`
  width: 34px;
  height: 34px;
  border-radius: 100%;
  margin-left: 10px;
  display: inline-block;
  ${media.s`
    display: none;
  `}
`;

ProfileIntro.propTypes = {
  routeName: PropTypes.string,
  appManager: PropTypes.object,
  t: PropTypes.func,
};

export default withNamespaces()(observer(ProfileIntro));
